/**
 * The app switcher (plan of record docs/plans/2026-10-13_microvision-and-the-account-layer.md, section A): the suite's
 * other products the member can open from the active org, each a link that names the org (lib/suite). It shows only
 * when there is one: an org holding Twin alone, or a build that does not know Foundry Scope's address, sees nothing.
 */

import { Check, ExternalLink, LayoutGrid } from "lucide-react";

import { useOrg } from "@/lib/org-context";
import { TWIN, heldProduct, switcherEntries, type SwitcherEntry } from "@/lib/suite";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/lib/ui/dropdown-menu";
import { Button } from "@/lib/ui/button";

function trialNote(e: SwitcherEntry): string | null {
  if (!e.trialUntil) return null;
  const until = new Date(e.trialUntil);
  if (Number.isNaN(until.getTime())) return "Trial";
  // The end's calendar date as stored (the close of that day in UTC), whatever the viewer's zone.
  const date = until.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
  return `Trial until ${date}`;
}

export function AppSwitcher({ productName }: { productName: string }) {
  const { activeOrg } = useOrg();
  const entries = switcherEntries(activeOrg?.orgId, activeOrg?.products);
  if (entries.length === 0) return null;
  const here = heldProduct(activeOrg?.products, TWIN)?.name || productName;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Switch product">
          <LayoutGrid className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>{activeOrg?.orgName}</DropdownMenuLabel>
        <DropdownMenuItem disabled className="opacity-100">
          <Check className="size-4 shrink-0 text-brand" />
          <span className="truncate">{here}</span>
        </DropdownMenuItem>
        {entries.map((e) => (
          <DropdownMenuItem key={e.key} asChild>
            <a href={e.href} className="flex items-center gap-2">
              <span className="size-4 shrink-0" />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate">{e.name}</span>
                {trialNote(e) && <span className="text-xs text-on-surface-muted">{trialNote(e)}</span>}
              </span>
              <ExternalLink className="size-3.5 shrink-0 text-on-surface-muted" />
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
